import React, { useEffect, useRef, useState } from 'react';
import { Wallet, Loader2, AlertCircle, ShieldCheck, ArrowLeft, KeyRound, MailCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LOGIN } from '../constants/testIds/auth';
import api from '../lib/api';

const CAMPO = 'w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 '
  + 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 '
  + 'placeholder-slate-400 dark:placeholder-slate-500 '
  + 'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all';

const BOTAO = 'w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 '
  + 'hover:to-purple-700 text-white py-3 rounded-xl font-medium shadow-lg hover:shadow-xl '
  + 'transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2';

export default function Login() {
  const { user, login, loginSegundoFator } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [desafio, setDesafio] = useState(null);   // null = etapa da senha
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [recuperando, setRecuperando] = useState(false);   // tela de "esqueci a senha"
  const [pedidoEnviado, setPedidoEnviado] = useState(null);
  const campoCodigo = useRef(null);

  useEffect(() => { if (user) navigate('/'); }, [user, navigate]);
  useEffect(() => { if (desafio && campoCodigo.current) campoCodigo.current.focus(); }, [desafio]);

  const mensagemDeErro = (err, padrao) =>
    err?.response?.data?.detail || padrao;

  const enviarSenha = async (e) => {
    e.preventDefault();
    if (enviando) return;
    setErro(''); setEnviando(true);
    try {
      const r = await login(email, senha);
      if (r.requires2fa) {
        setDesafio(r.challenge);
        setSenha('');
      } else {
        navigate('/');
      }
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não foi possível entrar. Tente novamente.'));
      setSenha('');
    } finally {
      setEnviando(false);
    }
  };

  const enviarCodigo = async (e) => {
    e.preventDefault();
    if (enviando) return;
    setErro(''); setEnviando(true);
    try {
      const r = await loginSegundoFator(desafio, codigo);
      if (r.recovery_code_used) {
        setAviso(`Você entrou com um código de recuperação. Restam ${r.recovery_codes_left}.`);
      }
      navigate('/');
    } catch (err) {
      setErro(mensagemDeErro(err, 'Código inválido.'));
      setCodigo('');
      // Desafio expirado: volta para a senha
      if (err?.response?.data?.detail?.includes('expirada')) setDesafio(null);
    } finally {
      setEnviando(false);
    }
  };

  const voltar = () => { setDesafio(null); setCodigo(''); setErro(''); };

  const pedirRecuperacao = async (e) => {
    e.preventDefault();
    if (enviando) return;
    setErro(''); setEnviando(true);
    try {
      const { data } = await api.post('/auth/recuperar', { email });
      // A resposta é a mesma exista o e-mail ou não — de propósito.
      setPedidoEnviado(data);
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não foi possível concluir o pedido. Tente de novo em instantes.'));
    } finally {
      setEnviando(false);
    }
  };

  const voltarDaRecuperacao = () => {
    setRecuperando(false); setPedidoEnviado(null); setErro('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8 border border-indigo-100 dark:border-slate-700">
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-4 rounded-2xl shadow-lg">
              {recuperando ? <KeyRound className="w-12 h-12 text-white" />
                : desafio ? <ShieldCheck className="w-12 h-12 text-white" />
                : <Wallet className="w-12 h-12 text-white" />}
            </div>
          </div>
          <h1 className="text-3xl font-bold text-center mb-2 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">D'Pots</h1>

          {recuperando && (
            <>
              {!pedidoEnviado ? (
                <>
                  <p className="text-gray-600 dark:text-slate-400 text-center mb-8">
                    Informe seu e-mail e enviaremos um endereço para você criar uma senha nova.
                  </p>
                  <form onSubmit={pedirRecuperacao} className="space-y-4">
                    <div>
                      <label htmlFor="email-rec" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">E-mail</label>
                      <input id="email-rec" data-testid="login-recuperar-email" type="email" autoComplete="username"
                        autoFocus required value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="voce@exemplo.com" className={CAMPO} />
                    </div>
                    {erro && (
                      <div role="alert" className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl px-3 py-2.5">
                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>{erro}</span>
                      </div>
                    )}
                    <button type="submit" data-testid="login-recuperar-enviar" disabled={enviando} className={BOTAO}>
                      {enviando && <Loader2 className="w-4 h-4 animate-spin" />}
                      {enviando ? 'Enviando...' : 'Enviar instruções'}
                    </button>
                  </form>
                </>
              ) : (
                <div className="space-y-4" data-testid="login-recuperar-enviado">
                  <div className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-3">
                    <MailCheck className="w-5 h-5 mt-0.5 shrink-0 text-emerald-600" />
                    <span>{pedidoEnviado.mensagem}</span>
                  </div>
                  {!pedidoEnviado.email_configurado && (
                    <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-xl px-3 py-2.5">
                      O envio automático ainda não está configurado neste sistema.
                      Peça ao administrador que gere um link de redefinição para você.
                    </p>
                  )}
                </div>
              )}

              <button type="button" onClick={voltarDaRecuperacao} data-testid="login-recuperar-voltar"
                className="w-full flex items-center justify-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 py-3 mt-2">
                <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao login
              </button>
            </>
          )}

          {!desafio && !recuperando && (
            <>
              <p className="text-gray-600 dark:text-slate-400 text-center mb-8">O sistema de gestão financeira mais eficiente que existe</p>

              <form onSubmit={enviarSenha} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">E-mail</label>
                  <input id="email" data-testid={LOGIN.emailInput} type="email" autoComplete="username"
                    autoFocus required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="voce@exemplo.com" className={CAMPO} />
                </div>

                <div>
                  <label htmlFor="senha" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Senha</label>
                  <input id="senha" data-testid={LOGIN.passwordInput} type="password" autoComplete="current-password"
                    required value={senha} onChange={(e) => setSenha(e.target.value)}
                    placeholder="••••••••" className={CAMPO} />
                </div>

                {erro && (
                  <div role="alert" className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl px-3 py-2.5">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{erro}</span>
                  </div>
                )}

                <button type="submit" data-testid={LOGIN.submitButton} disabled={enviando} className={BOTAO}>
                  {enviando && <Loader2 className="w-4 h-4 animate-spin" />}
                  {enviando ? 'Entrando...' : 'Entrar'}
                </button>
              </form>

              <button type="button" onClick={() => { setRecuperando(true); setErro(''); }}
                data-testid="login-esqueci-senha"
                className="w-full text-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 py-3 mt-2">
                Esqueci minha senha
              </button>
            </>
          )}

          {desafio && (
            <>
              <p className="text-gray-600 dark:text-slate-400 text-center mb-8">
                Digite o código de 6 dígitos do seu aplicativo autenticador
              </p>

              <form onSubmit={enviarCodigo} className="space-y-4">
                <div>
                  <label htmlFor="codigo" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Código de verificação
                  </label>
                  <input
                    id="codigo"
                    data-testid="login-2fa-input"
                    ref={campoCodigo}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                    placeholder="000000"
                    className={`${CAMPO} text-center text-2xl tracking-[0.4em] font-mono`}
                  />
                </div>

                {erro && (
                  <div role="alert" className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl px-3 py-2.5">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{erro}</span>
                  </div>
                )}

                <button type="submit" data-testid="login-2fa-submit" disabled={enviando} className={BOTAO}>
                  {enviando && <Loader2 className="w-4 h-4 animate-spin" />}
                  {enviando ? 'Verificando...' : 'Verificar'}
                </button>

                <button type="button" onClick={voltar}
                  className="w-full flex items-center justify-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 py-2">
                  <ArrowLeft className="w-3.5 h-3.5" /> Voltar
                </button>
              </form>

              <p className="text-xs text-gray-500 dark:text-slate-500 text-center mt-6">
                Perdeu o celular? Use um dos seus<br />códigos de recuperação no campo acima.
              </p>
            </>
          )}

          {aviso && (
            <div className="mt-4 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-xl px-3 py-2.5">
              {aviso}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
